import type { StartLocationSnapshot } from "@/hooks/useCurrentCleaningTask";

/** Session: last in-progress turnover URL so cleaners can resume after leaving the flow. */
export const CLEANER_RESUME_CLEAN_URL_KEY = "stravo:cleanerResumeCleanUrl";

/** Local draft for pre-visit wizard (step + GPS snapshot) across refresh / brief offline. */
export function previsitStorageKey(taskId: string) {
  return `stravo:previsitWizard:${taskId}`;
}

export type PrevisitWizardDraft = {
  v: 1;
  step: 0 | 1 | 2;
  preVisitLocation?: StartLocationSnapshot | null;
};

export function readPrevisitWizardDraft(taskId: string): PrevisitWizardDraft | null {
  try {
    const raw = localStorage.getItem(previsitStorageKey(taskId));
    if (!raw) return null;
    const p = JSON.parse(raw) as PrevisitWizardDraft;
    if (p?.v !== 1 || typeof p.step !== "number") return null;
    const step = (p.step === 0 || p.step === 1 || p.step === 2 ? p.step : 0) as 0 | 1 | 2;
    return { v: 1, step, preVisitLocation: p.preVisitLocation ?? null };
  } catch {
    return null;
  }
}

export function writePrevisitWizardDraft(taskId: string, draft: PrevisitWizardDraft) {
  try {
    localStorage.setItem(previsitStorageKey(taskId), JSON.stringify(draft));
  } catch {
    /* quota / private mode */
  }
}

export function clearPrevisitWizardDraft(taskId: string) {
  try {
    localStorage.removeItem(previsitStorageKey(taskId));
  } catch {
    /* ignore */
  }
}

export function setCleanerResumeCleanUrl(path: string) {
  try {
    sessionStorage.setItem(CLEANER_RESUME_CLEAN_URL_KEY, path);
  } catch {
    /* ignore */
  }
}

export function clearCleanerResumeCleanUrl() {
  try {
    sessionStorage.removeItem(CLEANER_RESUME_CLEAN_URL_KEY);
  } catch {
    /* ignore */
  }
}

export function getCleanerResumeCleanUrl(): string | null {
  try {
    return sessionStorage.getItem(CLEANER_RESUME_CLEAN_URL_KEY);
  } catch {
    return null;
  }
}

/** Parse `/clean/{propertyId}?taskId=...` stored in session resume URL. */
export function parseCleanerResumeCleanUrl(path: string): { propertyId: string; taskId: string } | null {
  if (!path?.trim()) return null;
  try {
    const normalized = path.trim();
    const propertyMatch = normalized.match(/^\/clean\/([^/?#]+)/);
    if (!propertyMatch?.[1]) return null;
    const query = normalized.includes("?") ? normalized.slice(normalized.indexOf("?")) : "";
    const taskId = new URLSearchParams(query).get("taskId");
    if (!taskId) return null;
    return { propertyId: propertyMatch[1], taskId };
  } catch {
    return null;
  }
}

/** Drop resume URL + pre-visit draft when a turnover no longer exists or was removed. */
export function clearCleanerTurnoverSessionForTask(taskId: string) {
  const stored = getCleanerResumeCleanUrl();
  if (stored) {
    const parsed = parseCleanerResumeCleanUrl(stored);
    if (parsed?.taskId === taskId) clearCleanerResumeCleanUrl();
  }
  clearPrevisitWizardDraft(taskId);
}
